import React, { useState, useEffect, useRef } from 'react';
import {
  loadGoogleApis, signInWithGoogle, getAccessToken,
  uploadFileToDrive, listStudentFiles, deleteFileFromDrive,
  formatFileSize, fileIcon,
} from '../../lib/googleDrive';

export default function GoogleDriveTab({ student }) {
  const [files, setFiles]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [connected, setConnected]   = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError]           = useState('');
  const fileInputRef                = useRef(null);

  const studentName = student?.full_name || 'Unknown';
  const regNo       = student?.register_number || 'UNKNOWN';

  // Load Google APIs on mount
  useEffect(() => {
    loadGoogleApis().then(() => {
      // Check if already connected
      if (getAccessToken()) { setConnected(true); loadFiles(); }
    });
  }, []);

  async function connect() {
    setConnecting(true);
    setError('');
    try {
      await signInWithGoogle();
      setConnected(true);
      await loadFiles();
    } catch (e) {
      setError('Could not connect to Google Drive. Please try again.');
    }
    setConnecting(false);
  }

  async function loadFiles() {
    setLoading(true);
    try {
      const f = await listStudentFiles(studentName, regNo);
      setFiles(f);
    } catch (e) {
      setError('Error loading files.');
    }
    setLoading(false);
  }

  async function handleFileSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadProgress(0);
    setError('');
    try {
      await uploadFileToDrive(file, studentName, regNo, setUploadProgress);
      await loadFiles();
    } catch (e) {
      setError('Upload failed. Please try again.');
    }
    setUploading(false);
    setUploadProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleDelete(fileId, fileName) {
    if (!window.confirm(`Delete "${fileName}" from Google Drive?`)) return;
    try {
      await deleteFileFromDrive(fileId);
      setFiles(f => f.filter(x => x.id !== fileId));
    } catch { setError('Delete failed.'); }
  }

  // Not connected state
  if (!connected) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>☁️</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Connect to Google Drive</div>
        <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
          Upload and manage documents for <strong>{studentName}</strong> directly in Google Drive.
          Files are organized in a dedicated folder per student.
        </div>
        {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>{error}</div>}
        <button className="btn btn-primary" onClick={connect} disabled={connecting} style={{ padding: '10px 28px', fontSize: 14 }}>
          {connecting ? '⏳ Connecting...' : '🔗 Connect Google Drive'}
        </button>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 12 }}>
          You'll be asked to sign in with your Google account
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>📁 Google Drive Documents</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
            Folder: <span style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>ARMSTECH ERP / {regNo} - {studentName}</span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={loadFiles} disabled={loading}>
            {loading ? '⏳' : '↻'} Refresh
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? `⏳ ${uploadProgress}%` : '📤 Upload File'}
          </button>
          <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx,.ppt,.pptx,.zip,.txt" />
        </div>
      </div>

      {/* Upload progress bar */}
      {uploading && (
        <div style={{ marginBottom: 16, background: 'var(--bg3)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Uploading... {uploadProgress}%</div>
          <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${uploadProgress}%`, background: 'var(--accent)', borderRadius: 3, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', borderRadius: 6 }}>{error}</div>}

      {/* Files list */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : files.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📂</div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>No documents yet</div>
          <div style={{ fontSize: 12 }}>Click "Upload File" to add documents for this student</div>
        </div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {/* Column headers */}
          <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 100px 140px 100px', gap: 12, padding: '8px 14px', background: 'var(--accent-light)', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <span></span>
            <span>File Name</span>
            <span>Size</span>
            <span>Uploaded</span>
            <span style={{ textAlign: 'center' }}>Actions</span>
          </div>

          {files.map((file, i) => (
            <div key={file.id} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 100px 140px 100px', gap: 12, padding: '10px 14px', borderTop: i > 0 ? '1px solid var(--border)' : 'none', alignItems: 'center', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
              <span style={{ fontSize: 20 }}>{fileIcon(file.mimeType)}</span>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{file.mimeType?.split('/')[1]?.toUpperCase() || 'FILE'}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>{formatFileSize(Number(file.size))}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                {file.createdTime ? new Date(file.createdTime).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <a href={file.webViewLink} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm" title="View in Google Drive" style={{ fontSize: 16, textDecoration: 'none' }}>👁️</a>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)', fontSize: 16 }} onClick={() => handleDelete(file.id, file.name)} title="Delete">🗑️</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      {files.length > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text3)', textAlign: 'right' }}>
          {files.length} file{files.length !== 1 ? 's' : ''} · Stored in Google Drive
        </div>
      )}
    </div>
  );
}
